/**
 * SettingsPage — `settings.html`, WEB TIER A · WT7.
 *
 * ===========================================================================
 * TWO SECTIONS. NOT THREE, AND NOT TWO PLUS A PROMISE.
 * ===========================================================================
 * Owner ruling 3: Settings is **Profile and Appearance only**. Notifications
 * and Sessions were considered and deselected, so they are ABSENT — not greyed
 * out, not "coming soon". The mockup's own harness re-reads its section titles
 * off the DOM and asserts `Profile|Appearance`; `settings.test.tsx` mirrors that
 * check against this component, which is why the main column carries a
 * `data-testid`: the aside's context cards are headings too, and a page-wide
 * count would pass for the wrong reason.
 *
 * ===========================================================================
 * A ROUTE, NOT A DESTINATION
 * ===========================================================================
 * `domain/nav.ts` has no settings item and gains none (plan §11, "no new IA").
 * The page is reached from the sidebar identity block — `ProfileMenu`'s new
 * "Settings" item — which is where the shell already puts a caret. The mockup
 * asserts the sidebar still draws its 19 destinations in 6 sections here.
 *
 * ===========================================================================
 * WHAT IS INERT, AND WHY IT IS A RIM RATHER THAN A DIM
 * ===========================================================================
 * Four of the five Profile rows belong to a group administrator on Admin →
 * Users. They render bordered-inert: a DASHED `border/strong` rim with the
 * value at FULL contrast, never an opacity fade (the sandbox measured
 * `disabled:opacity-50` at 2.11:1, and WCAG 1.4.11 excepts an inactive
 * component's rim). Each one names where the change is made, which is MC-7's
 * open question answered the same way on every screen in the approved set.
 *
 * ┌── FRAME vs SCHEMA, TWICE, RECORDED RATHER THAN PAPERED OVER ─────────────┐
 * │ (1) THE ROW SET. `settings.html` draws Name / Email / Phone / Photo /     │
 * │     Password; plan §6 and this cluster's brief name "name/email/role/     │
 * │     country inert + password". Both are FIVE rows and they differ by two. │
 * │     Built here: Name, Email, Phone, Role and country, Password — the      │
 * │     brief's four inert subjects (role and country in ONE row, exactly as  │
 * │     the mockup's own provenance table pairs them: "Role and country ·     │
 * │     One country role"), plus the mockup's Phone row, which is a REAL      │
 * │     nullable column on `MeResponse` and is the mockup's demonstration of  │
 * │     NO_VALUE. The one drawn row deliberately NOT built is PHOTO: the      │
 * │     self-serve uploader already exists and already works, in              │
 * │     `components/shell/ProfileMenu.tsx` (upload / replace / remove, with   │
 * │     the server's own refusals rendered). A second uploader wiring would   │
 * │     be new scope for no new capability, so the aside NAMES where the      │
 * │     photo is changed instead. Flagged in the cluster report, not folded.  │
 * │                                                                          │
 * │ (2) THE PASSWORD SENTENCE. The mockup's help line reads "Changing it      │
 * │     signs you out of every other browser and device; this one stays       │
 * │     signed in." MEASURED against the API: `AuthService.changePassword`    │
 * │     calls `revokeAllForUser(user.id)`, which revokes EVERY non-revoked    │
 * │     refresh token for the user with no exception for the caller's own     │
 * │     device (`auth.service.ts` — the same helper `logout` uses for         │
 * │     scope=all). This browser's refresh token dies with the rest; the      │
 * │     access token outlives it only until it expires. So the sentence this  │
 * │     page prints is the true one — every browser, including this one —     │
 * │     and the count the server returns is printed with it.                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ===========================================================================
 * APPEARANCE HAS NO SAVE
 * ===========================================================================
 * Theme and density are kept in this browser and take effect on the keystroke;
 * a Save button would imply a round trip that does not happen. The card says
 * so. Theme is `lib/theme.tsx` (the third option is the ABSENCE of a stored
 * mode — see its header); density is `lib/density.ts` plus the
 * `--crf-density-*` stops in `tokens.css`.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '../../auth/session';
import { useCountryCode } from '../../auth/guards';
import { countryProfile } from '../../domain/countries';
import { cn } from '../../lib/cn';
import { useTheme } from '../../lib/theme';
import type { ThemeChoice } from '../../lib/theme';
import {
  DENSITY_OPTIONS,
  currentDensity,
  setDensity as persistDensity,
} from '../../lib/density';
import type { Density } from '../../lib/density';
import { ChevronRight } from 'lucide-react';
import { Chip, KvList, KvRow, PageHead, RecordCard } from '../../components/ui';
import { PasswordRow } from './PasswordRow';

/**
 * What an absent value renders as. A third copy of the same em dash, and
 * deliberately: `features/admin/user-format.ts` states the app's convention in
 * its own header — features do not import from each other's folders, the
 * shared surface is `lib/` and `components/ui`, and one constant is cheaper
 * than a cross-stream dependency.
 */
const NO_VALUE = '—';

/** Every inert row says the same thing about where its value is changed. */
const CHANGED_BY_ADMIN = 'Changed by a group administrator, on Admin then Users.';

// ---------------------------------------------------------------- pieces ----

function Help({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="text-label text-fg-secondary">{children}</p>;
}

/**
 * A read-only Profile row. The value sits inside a dashed rim at full
 * contrast — bordered-inert, the WT1 control-rim rule — and is exposed to AT
 * through its own label rather than as a disabled input, because it is not a
 * control at all: there is nothing here to operate.
 */
function InertRow({
  label,
  value,
  help,
}: {
  label: string;
  value: string;
  help: string;
}): React.JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="flex items-center gap-2 text-label font-medium text-fg">
        {label}
        <span className="inline-flex h-[22px] items-center rounded-pill bg-subtle px-2 text-label font-medium text-fg">
          Read only
        </span>
      </span>
      <span
        data-inert-value
        className="flex min-h-9 w-full items-center rounded-sm border border-dashed border-line-strong px-3 text-body font-medium text-fg"
      >
        {value}
      </span>
      <Help>{help}</Help>
    </div>
  );
}

interface CardOption<T extends string> {
  value: T;
  label: string;
  detail: string;
}

/**
 * The 1-of-N option cards (`settings.html` `.opt`). SELECTED IS THREE
 * ENCODINGS, never a colour: a 2px accent rim (4.22 against the card, clearing
 * 1.4.11), a filled dot, and `aria-checked`. Remove the colour and two working
 * signals remain.
 *
 * Hand-rolled rather than vendored: none of the four WT2 compositions is a
 * radio group, and Radix's own RadioGroup is not an installed dependency —
 * adding a package to draw three cards would be scope, not reuse. The ARIA
 * radiogroup pattern is implemented in full instead: roving tabindex (only the
 * checked card is a tab stop) and arrow keys that move the selection, which is
 * what a keyboard reader expects from a group of radios.
 */
function OptionCards<T extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: readonly CardOption<T>[];
  value: T;
  onChange: (next: T) => void;
}): React.JSX.Element {
  const move = (delta: number): void => {
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + delta + options.length) % options.length];
    if (next !== undefined) onChange(next.value);
  };

  return (
    <div role="radiogroup" aria-label={legend} className="flex flex-col gap-2.5 sm:flex-row">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                move(1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(-1);
              }
            }}
            className={cn(
              'flex min-w-0 flex-1 gap-2.5 rounded-sm bg-surface p-3 text-left',
              selected
                ? 'border-2 border-indigo p-[11px]'
                : 'border border-line-strong hover:border-fg-secondary',
            )}
          >
            <span
              className={cn(
                'mt-px flex size-4 shrink-0 items-center justify-center rounded-pill border',
                selected ? 'border-indigo' : 'border-fg-secondary',
              )}
            >
              {selected ? <span className="size-2 rounded-pill bg-indigo" /> : null}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-body13 font-medium text-fg">{option.label}</span>
              <span className="text-label text-fg-secondary">{option.detail}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The density preview — `settings.html` screen 3, reduced to ONE row because
 * the page has a live control and the document did not: the mockup drew the
 * same row three times to show three stops at once, whereas here the reader
 * picks a stop and the row redraws.
 *
 * ===========================================================================
 * ⚠ THE PREVIEW MUST NOT LIE, AND IT USED TO — REWRITTEN 2026-08-18 (OQ-1)
 * ===========================================================================
 * This row was a SINGLE-LINE ledger row (customer · total · number) carrying an
 * inline `minHeight` style and three inline font-size styles of its own. That
 * was honest only while it previewed nothing: the mockup drew a single-line row
 * and the registers were deliberately unwired, so the preview was the mockup's
 * artefact rather than a promise about the app.
 *
 * With OQ-1 ruled and every register now on `--crf-density-row`, a preview that
 * does not LOOK like a register row is a surface advertising a different
 * surface — the same defect class as the "masked seat" finding. So the row
 * below is the WORKLIST row's own two-line grammar: line 1 the customer at the
 * lead size with a status chip, line 2 `docNo · type · raised {date}` at the
 * quiet sizes, then the money slot, the age cell and the chevron.
 *
 * IT WEARS `crf-register-row`, THE SAME CLASS THE REAL ROWS WEAR, and it no
 * longer styles its own height or type. That is the whole anti-drift argument:
 * there is no second place where the preview's geometry could be edited, so a
 * stop cannot move the registers without moving this row by exactly as much.
 * `settings.test.tsx` pins the class and the two-line structure against
 * `QuotesListPage`'s row so the agreement cannot rot silently.
 *
 * The sample values are the mockup's own (`settings.html` screen 3).
 */
function DensityPreview(): React.JSX.Element {
  return (
    <div className="overflow-hidden rounded-sm border border-line">
      <div className="flex h-9 items-center gap-3 border-b border-line bg-subtle px-4">
        <span className="min-w-0 flex-1 text-th uppercase text-fg">Customer</span>
        <span className="w-[180px] text-right text-th uppercase text-fg">Grand total</span>
        <span className="w-16 text-right text-th uppercase text-fg">Age</span>
      </div>
      <div
        data-density-preview
        className="crf-register-row flex items-center gap-3 bg-surface px-4"
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="truncate text-body font-medium text-fg">Kataria Automobile</span>
            <Chip>Sent</Chip>
          </div>
          <p className="mt-0.5 truncate text-label text-fg-secondary">
            <span className="font-mono text-mono">IN-QT/2627/0007</span>
            {' · Quotation · raised 3 March 2026'}
          </p>
        </div>
        <span className="w-[180px] shrink-0 text-right text-h3 text-fg tnum">₹1,18,650</span>
        <span className="w-16 shrink-0 text-right text-body13 text-fg-secondary tnum">4d</span>
        <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-fg-secondary" />
      </div>
    </div>
  );
}

// ------------------------------------------------------------ the password --
//
// `PasswordRow` lives in `./PasswordRow.tsx` since design U8 item 11: the session provider
// renders the same form full-screen for a must-change session, and this page imports the
// provider (`useSession`), so the form had to move out to keep the module graph acyclic.
// The row on this page is unchanged; its own file carries the recon and the argument.

// ------------------------------------------------------------------ page ----

export function SettingsPage(): React.JSX.Element {
  const { me } = useSession();
  const countryCode = useCountryCode();
  const country = countryProfile(countryCode);
  const { theme, choice, setChoice } = useTheme();
  const [density, setDensityState] = useState<Density>(() => currentDensity());

  const name = me?.name ?? NO_VALUE;
  const email = me?.email ?? NO_VALUE;
  const phone = me?.phone ?? null;
  // The role in the country the reader is IN. A group administrator holds no
  // per-country scope row, so the honest answer there is the standing they
  // actually have — not a blank, and not a role they were never given.
  const scopedRole = me?.scopes.find((scope) => scope.c === countryCode)?.r ?? null;
  const role = scopedRole ?? (me?.is_group_admin === true ? 'Group administrator' : NO_VALUE);

  const chooseDensity = (nextDensity: Density): void => {
    persistDensity(nextDensity);
    setDensityState(nextDensity);
  };

  const themeOptions: readonly CardOption<ThemeChoice>[] = [
    { value: 'light', label: 'Light', detail: 'The collection Light mode' },
    { value: 'dark', label: 'Dark', detail: 'The seven variables Dark re-maps' },
    {
      value: 'system',
      label: 'Follow this computer',
      detail: `Currently ${theme}. Changes when the computer does`,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title="Settings"
        sub={`Signed in as ${name}, ${role} for ${country.name}. Two sections.`}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div
          data-testid="settings-sections"
          className="flex min-w-0 flex-1 flex-col gap-4"
        >
          {/* The identity block — the record's own arrangement, and DELIBERATELY
           * headerless: it is who the page is about, not a section of it, so
           * the name is a paragraph at H1 SIZE rather than a heading. The page
           * already has exactly one h1 ("Settings", from PageHead) and the two
           * section headings are the two h2s below; a third heading here would
           * put the reader's own name in the document outline as if it were a
           * section, and would break the mockup's two-section count. */}
          <RecordCard>
            <div className="flex min-w-0 items-center gap-3">
              <p className="min-w-0 flex-1 truncate text-h1 text-fg">{name}</p>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2.5">
              <span className="inline-flex h-[22px] items-center rounded-sm border border-line bg-subtle px-2 font-mono text-mono text-fg">
                {email}
              </span>
              <span className="text-body13 text-fg-secondary">
                {role} · {country.name}
              </span>
            </div>
            <p className="text-body13 text-fg-secondary">
              The four fields below that a group administrator owns are read-only here.
            </p>
          </RecordCard>

          <RecordCard
            title="Profile"
            sub="Your record. Four fields are changed by a group administrator."
          >
            <div className="grid gap-3.5 sm:grid-cols-2">
              <InertRow label="Name" value={name} help={CHANGED_BY_ADMIN} />
              <InertRow
                label="Email"
                value={email}
                help={`This address is how you sign in. ${CHANGED_BY_ADMIN}`}
              />
            </div>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <InertRow
                label="Phone"
                value={phone ?? NO_VALUE}
                help={
                  phone === null
                    ? `No phone number is recorded: it is optional on the user record and none was given. ${CHANGED_BY_ADMIN}`
                    : CHANGED_BY_ADMIN
                }
              />
              <InertRow
                label="Role and country"
                value={`${role} · ${country.name}`}
                help={`Roles are country-scoped by construction. ${CHANGED_BY_ADMIN}`}
              />
            </div>
            <PasswordRow />
          </RecordCard>

          <RecordCard title="Appearance" sub="Kept in this browser">
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="text-label font-medium text-fg">Theme</span>
              <OptionCards
                legend="Theme"
                options={themeOptions}
                value={choice}
                onChange={setChoice}
              />
              <Help>
                Kept in this browser. It will not follow you to another computer, and
                anybody else who signs in here gets your choice.
              </Help>
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="text-label font-medium text-fg">Text size and density</span>
              <OptionCards
                legend="Text size and density"
                options={DENSITY_OPTIONS}
                value={density}
                onChange={chooseDensity}
              />
              <DensityPreview />
              {/*
                RULE-5 CALLOUT — THIS COPY WAS FALSE THE MOMENT THE REGISTERS WERE WIRED.
                It read: "It is the only surface using it today — the registers keep their
                own row height for now." That was true under OQ-1 and is not true now: the
                preview above and every register row are the same class on the same token.
                Corrected here rather than left to age, because a Help line that under-
                promises teaches the reader the control does nothing.
              */}
              <Help>
                The row above is drawn at the setting you pick, so the effect is visible
                before it is lived with. It is the row every register draws — quotations,
                inquiries, CRFs, renewals, licences and the admin tables all follow it.
              </Help>
            </div>
          </RecordCard>
        </div>

        <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
          <RecordCard title="Where each setting lives">
            <KvList>
              <KvRow label="Name, email, phone">The user record</KvRow>
              <KvRow label="Role and country">The user record</KvRow>
              <KvRow label="Password">The user record</KvRow>
              <KvRow label="Photo">The menu you came from</KvRow>
              <KvRow label="Theme">This browser</KvRow>
              <KvRow label="Density">This browser</KvRow>
            </KvList>
            <Help>
              The split matters: the top rows follow you to any computer and the bottom
              two do not, which is why they are in different sections rather than one
              long list.
            </Help>
          </RecordCard>

          <RecordCard title="Your photo">
            <p className="text-body13 text-fg-secondary">
              It is changed where you opened this page: the identity block at the top of
              the sidebar carries Upload, Replace and Remove. JPEG, PNG or WebP, up to
              5&nbsp;MB, re-encoded to 256×256 on the server. Until you add one, your
              initials carry a colour computed from your name — not a choice.
            </p>
          </RecordCard>

          <RecordCard title="What is not on this page">
            <p className="text-body13 text-fg-secondary">
              Notifications and Sessions. Both were considered and both were deselected,
              so they are absent rather than disabled. A greyed-out section is a promise,
              and this page is not making one.
            </p>
          </RecordCard>
        </aside>
      </div>
    </div>
  );
}
